import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Skeleton,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Badge,
  Snackbar,
  Alert,
  SvgIcon,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "../utils/axios-instance";
import { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { ServerRoutes } from "../routes";
import {
  type User,
  type AppPagination,
  type Course,
  type CourseSeason,
  type CourseItem,
} from "../types";
import { useQuery } from "@tanstack/react-query";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";
import UsersMixedChart from "../components/UsersMixedChart";

// Telegram Icon Component
const TelegramIcon: React.FC = () => (
  <SvgIcon
    viewBox="0 0 256 256"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    preserveAspectRatio="xMidYMid"
  >
    <defs>
      <linearGradient id="a" x1="50%" x2="50%" y1="0%" y2="100%">
        <stop offset="0%" stopColor="#2AABEE" />
        <stop offset="100%" stopColor="#229ED9" />
      </linearGradient>
    </defs>
    <path
      fill="url(#a)"
      d="M128 0C94.06 0 61.48 13.494 37.5 37.49A128.038 128.038 0 0 0 0 128c0 33.934 13.5 66.514 37.5 90.51C61.48 242.506 94.06 256 128 256s66.52-13.494 90.5-37.49c24-23.996 37.5-56.576 37.5-90.51 0-33.934-13.5-66.514-37.5-90.51C194.52 13.494 161.94 0 128 0Z"
    />
    <path
      fill="#FFF"
      d="M57.94 126.648c37.32-16.256 62.2-26.974 74.64-32.152 35.56-14.786 42.94-17.354 47.76-17.441 1.06-.017 3.42.245 4.96 1.49 1.28 1.05 1.64 2.47 1.82 3.467.16.996.38 3.266.2 5.038-1.92 20.24-10.26 69.356-14.5 92.026-1.78 9.592-5.32 12.808-8.74 13.122-7.44.684-13.08-4.912-20.28-9.63-11.26-7.386-17.62-11.982-28.56-19.188-12.64-8.328-4.44-12.906 2.76-20.386 1.88-1.958 34.64-31.748 35.26-34.45.08-.338.16-1.598-.6-2.262-.74-.666-1.84-.438-2.64-.258-1.14.256-19.12 12.152-54 35.686-5.1 3.508-9.72 5.218-13.88 5.128-4.56-.098-13.36-2.584-19.9-4.708-8-2.606-14.38-3.984-13.82-8.41.28-2.304 3.46-4.662 9.52-7.072Z"
    />
  </SvgIcon>
);

type UserFilterParams = {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  search: string;
  watchedCourseItemId: number | null;
};

const buildUserFilterQuery = ({
  fromDate,
  toDate,
  search,
  watchedCourseItemId,
}: UserFilterParams) => {
  const query = new URLSearchParams();
  if (fromDate) query.append("from", fromDate.format("YYYY-MM-DD"));
  if (toDate) query.append("to", toDate.format("YYYY-MM-DD"));
  if (search.trim()) {
    query.append("search", search.trim());
  }
  if (watchedCourseItemId) {
    query.append("watchedCourseItemId", watchedCourseItemId.toString());
  }
  return query;
};

// 🔁 Fetch function
const fetchUsers = async ({
  page,
  ...filters
}: UserFilterParams & { page: number }) => {
  const query = buildUserFilterQuery(filters);
  query.append("page", page.toString());

  const response = await axios.get<AppPagination<User>>(
    `${ServerRoutes.users}?${query.toString()}`,
  );
  return response.data;
};

const fetchFilteredUserIds = async (filters: UserFilterParams) => {
  const query = buildUserFilterQuery(filters);
  const response = await axios.get<{ ids: number[]; count: number }>(
    `${ServerRoutes.users}/ids?${query.toString()}`,
  );
  return response.data;
};

const fetchFilterCourses = async () => {
  const response = await axios.get<Course[]>(ServerRoutes.courses);
  return response.data;
};

const fetchFilterSeasons = async (courseId: number) => {
  const response = await axios.get<CourseSeason[]>(
    `${ServerRoutes.courseSeasons}?courseId=${courseId}`,
  );
  return response.data;
};

const fetchFilterCourseItems = async (courseId: number) => {
  const response = await axios.get<CourseItem[]>(
    `${ServerRoutes.courseItems}?courseId=${courseId}`,
  );
  return response.data;
};

export function UserSection() {
  type NotificationAttachmentKind =
    | "none"
    | "image"
    | "video"
    | "voice"
    | "file";
  type TelegramAttachmentKind = "none" | "image" | "video" | "voice" | "file";
  type NotificationAttachmentItem = {
    attachmentType: NotificationAttachmentKind;
    attachmentUrl: string;
    fileName: string;
    previewUrl: string;
    previewKind: NotificationAttachmentKind;
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Filter modal state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Dayjs | null>(null);
  const [tempToDate, setTempToDate] = useState<Dayjs | null>(null);
  const [watchedCourseId, setWatchedCourseId] = useState<number | null>(null);
  const [watchedSeasonId, setWatchedSeasonId] = useState<number | null>(null);
  const [watchedCourseItemId, setWatchedCourseItemId] = useState<number | null>(
    null,
  );
  const [watchedCourseItemLabel, setWatchedCourseItemLabel] = useState("");
  const [tempWatchedCourseId, setTempWatchedCourseId] = useState<number | null>(
    null,
  );
  const [tempWatchedSeasonId, setTempWatchedSeasonId] = useState<number | null>(
    null,
  );
  const [tempWatchedCourseItemId, setTempWatchedCourseItemId] = useState<
    number | null
  >(null);

  // Add state for copy notification
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  // Telegram modal state
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [telegramMessage, setTelegramMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [telegramSnackbarOpen, setTelegramSnackbarOpen] = useState(false);
  const [telegramSnackbarMessage, setTelegramSnackbarMessage] = useState("");
  const [telegramSnackbarSeverity, setTelegramSnackbarSeverity] = useState<
    "success" | "error"
  >("success");
  const [isTelegramBroadcastModalOpen, setIsTelegramBroadcastModalOpen] =
    useState(false);
  const [telegramBroadcastMessage, setTelegramBroadcastMessage] = useState("");
  const [telegramParseMode, setTelegramParseMode] = useState<
    "HTML" | undefined
  >(undefined);
  const [telegramDisableWebPagePreview, setTelegramDisableWebPagePreview] =
    useState(false);
  const [telegramBroadcastTarget, setTelegramBroadcastTarget] = useState<
    "all" | "selected"
  >("all");
  const [telegramAttachmentType, setTelegramAttachmentType] =
    useState<TelegramAttachmentKind>("none");
  const [telegramAttachmentUrl, setTelegramAttachmentUrl] = useState("");
  const [telegramAttachmentFileName, setTelegramAttachmentFileName] =
    useState("");
  const [telegramAttachmentPreviewUrl, setTelegramAttachmentPreviewUrl] =
    useState("");
  const [telegramAttachmentPreviewKind, setTelegramAttachmentPreviewKind] =
    useState<"none" | "image" | "video" | "voice" | "file">("none");
  const [telegramUploadProgress, setTelegramUploadProgress] = useState(0);
  const [isTelegramUploading, setIsTelegramUploading] = useState(false);
  const [isTelegramRecording, setIsTelegramRecording] = useState(false);
  const [telegramRecordingSeconds, setTelegramRecordingSeconds] = useState(0);
  const [isTelegramBroadcastSending, setIsTelegramBroadcastSending] =
    useState(false);
  const telegramUploadAbortController = useRef<AbortController | null>(null);
  const telegramMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const telegramMediaStreamRef = useRef<MediaStream | null>(null);
  const telegramRecordingChunksRef = useRef<BlobPart[]>([]);
  const telegramRecordingTimerRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const telegramRecordingCanceledRef = useRef(false);
  const telegramMessageInputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);

  // Notification modal state
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationLink, setNotificationLink] = useState("");
  const [notificationLinkText, setNotificationLinkText] = useState("");
  const [notificationTarget, setNotificationTarget] = useState<
    "all" | "selected"
  >("all");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [, setNotificationAttachmentType] =
    useState<NotificationAttachmentKind>("none");
  const [, setNotificationAttachmentUrl] = useState("");
  const [, setNotificationAttachmentFileName] = useState("");
  const [
    notificationAttachmentPreviewUrl,
    setNotificationAttachmentPreviewUrl,
  ] = useState("");
  const [, setNotificationAttachmentPreviewKind] =
    useState<NotificationAttachmentKind>("none");
  const [notificationAttachments, setNotificationAttachments] = useState<
    NotificationAttachmentItem[]
  >([]);
  const [notificationUploadProgress, setNotificationUploadProgress] =
    useState(0);
  const [isNotificationUploading, setIsNotificationUploading] = useState(false);
  const [isNotificationRecording, setIsNotificationRecording] = useState(false);
  const [notificationRecordingSeconds, setNotificationRecordingSeconds] =
    useState(0);
  const [isNotificationSending, setIsNotificationSending] = useState(false);
  const [notificationSnackbarOpen, setNotificationSnackbarOpen] =
    useState(false);
  const [notificationSnackbarMessage, setNotificationSnackbarMessage] =
    useState("");
  const [notificationSnackbarSeverity, setNotificationSnackbarSeverity] =
    useState<"success" | "error">("success");
  const [isSelectingAllFiltered, setIsSelectingAllFiltered] = useState(false);
  const notificationUploadAbortController = useRef<AbortController | null>(
    null,
  );
  const notificationMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const notificationMediaStreamRef = useRef<MediaStream | null>(null);
  const notificationRecordingChunksRef = useRef<BlobPart[]>([]);
  const notificationRecordingTimerRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const notificationRecordingCanceledRef = useRef(false);

  // Explicitly typing useQuery
  const { data, isLoading, isFetching, isError, error } = useQuery<
    AppPagination<User>,
    Error
  >({
    queryKey: [
      "users",
      page,
      fromDate?.format(),
      toDate?.format(),
      debouncedSearch,
      watchedCourseItemId,
    ],
    queryFn: () =>
      fetchUsers({
        page,
        fromDate,
        toDate,
        search: debouncedSearch,
        watchedCourseItemId,
      }),
  });

  const { data: filterCourses = [], isLoading: areCoursesLoading } = useQuery({
    queryKey: ["user-filter-courses"],
    queryFn: fetchFilterCourses,
  });

  const { data: filterSeasons = [], isLoading: areSeasonsLoading } = useQuery({
    queryKey: ["user-filter-seasons", tempWatchedCourseId],
    queryFn: () => fetchFilterSeasons(tempWatchedCourseId as number),
    enabled: Boolean(tempWatchedCourseId),
  });

  const { data: allFilterCourseItems = [], isLoading: areItemsLoading } =
    useQuery({
      queryKey: ["user-filter-course-items", tempWatchedCourseId],
      queryFn: () => fetchFilterCourseItems(tempWatchedCourseId as number),
      enabled: Boolean(tempWatchedCourseId),
    });

  const filterCourseItems = tempWatchedSeasonId
    ? allFilterCourseItems.filter(
        (item) => item.season?.id === tempWatchedSeasonId,
      )
    : [];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const stopMediaStreamTracks = (streamRef: {
    current: MediaStream | null;
  }) => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const clearIntervalTimer = (timerRef: {
    current: ReturnType<typeof setInterval> | null;
  }) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resolveVoiceMimeType = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
    ];
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
  };

  // Fallbacks if `data` is undefined or malformed
  const users = data?.items ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;
  const visibleUserIds = users.map((user) => user.id);
  const allVisibleSelected =
    visibleUserIds.length > 0 &&
    visibleUserIds.every((id) => selectedUserIds.includes(id));
  const someVisibleSelected = visibleUserIds.some((id) =>
    selectedUserIds.includes(id),
  );
  const totalFilteredUsers = data?.meta?.totalItems ?? 0;
  const allFilteredSelected =
    totalFilteredUsers > 0 && selectedUserIds.length === totalFilteredUsers;

  const handleToggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleToggleSelectAllVisible = () => {
    setSelectedUserIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleUserIds.includes(id));
      }
      const merged = [...prev, ...visibleUserIds];
      return merged.filter((id, index) => merged.indexOf(id) === index);
    });
  };

  const handleToggleSelectAllFiltered = async () => {
    if (allFilteredSelected) {
      setSelectedUserIds([]);
      return;
    }

    setIsSelectingAllFiltered(true);
    try {
      const result = await fetchFilteredUserIds({
        fromDate,
        toDate,
        search: debouncedSearch,
        watchedCourseItemId,
      });
      setSelectedUserIds(result.ids);
    } catch (error) {
      console.error("Failed to select all filtered users:", error);
      setTelegramSnackbarMessage("Failed to select all filtered users.");
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
    } finally {
      setIsSelectingAllFiltered(false);
    }
  };

  const openFilterModal = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setTempWatchedCourseId(watchedCourseId);
    setTempWatchedSeasonId(watchedSeasonId);
    setTempWatchedCourseItemId(watchedCourseItemId);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const applyFilters = () => {
    const selectedCourse = filterCourses.find(
      (course) => course.id === tempWatchedCourseId,
    );
    const selectedSeason = filterSeasons.find(
      (season) => season.id === tempWatchedSeasonId,
    );
    const selectedItem = allFilterCourseItems.find(
      (item) => item.id === tempWatchedCourseItemId,
    );

    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setWatchedCourseId(tempWatchedCourseId);
    setWatchedSeasonId(tempWatchedSeasonId);
    setWatchedCourseItemId(tempWatchedCourseItemId);
    setWatchedCourseItemLabel(
      tempWatchedCourseItemId
        ? [
            selectedCourse?.name,
            selectedSeason?.name,
            selectedItem?.name || `Item ${tempWatchedCourseItemId}`,
          ]
            .filter(Boolean)
            .join(" / ")
        : "",
    );
    setSelectedUserIds([]);
    setPage(1);
    closeFilterModal();
  };

  const clearFilters = () => {
    setTempFromDate(null);
    setTempToDate(null);
    setTempWatchedCourseId(null);
    setTempWatchedSeasonId(null);
    setTempWatchedCourseItemId(null);
  };

  const hasActiveFilters = Boolean(
    fromDate || toDate || debouncedSearch.trim() || watchedCourseItemId,
  );

  // Calculate the number of active filters for badge
  const activeFilterCount = [
    fromDate ? 1 : 0,
    toDate ? 1 : 0,
    debouncedSearch.trim() ? 1 : 0,
    watchedCourseItemId ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);

  // Handle removing filters directly from chips
  const handleRemoveFromDate = () => {
    setFromDate(null);
    setSelectedUserIds([]);
    setPage(1);
  };

  const handleRemoveToDate = () => {
    setToDate(null);
    setSelectedUserIds([]);
    setPage(1);
  };

  const handleRemoveSearch = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedUserIds([]);
    setPage(1);
  };

  const handleRemoveWatchedCourseItem = () => {
    setWatchedCourseId(null);
    setWatchedSeasonId(null);
    setWatchedCourseItemId(null);
    setWatchedCourseItemLabel("");
    setSelectedUserIds([]);
    setPage(1);
  };

  // Add copy to clipboard function
  const handleCopyWalletAddress = (address: string) => {
    navigator.clipboard
      .writeText(address)
      .then(() => {
        setCopySnackbarOpen(true);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Function to truncate wallet address
  const truncateAddress = (address: string) => {
    if (!address) return "";
    if (address.length <= 12) return address;
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Add function to close snackbar
  const handleCloseSnackbar = () => {
    setCopySnackbarOpen(false);
  };

  // Telegram message handlers
  // const handleOpenTelegramModal = (userId: number) => {
  //   setSelectedUserId(userId);
  //   setTelegramMessage("");
  //   setIsTelegramModalOpen(true);
  // };

  const handleCloseTelegramModal = () => {
    setIsTelegramModalOpen(false);
    setSelectedUserId(null);
    setTelegramMessage("");
  };

  const handleSendTelegramMessage = async () => {
    if (!selectedUserId || !telegramMessage.trim()) return;

    setIsSendingMessage(true);
    try {
      await axios.post("/admin/send-telegram-message", {
        userId: selectedUserId,
        message: telegramMessage.trim(),
      });

      setTelegramSnackbarMessage("Telegram message sent successfully!");
      setTelegramSnackbarSeverity("success");
      setTelegramSnackbarOpen(true);
      handleCloseTelegramModal();
    } catch (error) {
      console.error("Failed to send telegram message:", error);
      setTelegramSnackbarMessage(
        "Failed to send telegram message. Please try again.",
      );
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleCloseTelegramSnackbar = () => {
    setTelegramSnackbarOpen(false);
  };

  const handleOpenTelegramBroadcastModal = () => {
    setTelegramBroadcastMessage("");
    setTelegramParseMode(undefined);
    setTelegramDisableWebPagePreview(false);
    setTelegramBroadcastTarget(selectedUserIds.length > 0 ? "selected" : "all");
    setTelegramAttachmentType("none");
    setTelegramAttachmentUrl("");
    setTelegramAttachmentFileName("");
    setTelegramAttachmentPreviewUrl("");
    setTelegramAttachmentPreviewKind("none");
    setTelegramUploadProgress(0);
    setIsTelegramRecording(false);
    setTelegramRecordingSeconds(0);
    setIsTelegramBroadcastModalOpen(true);
  };

  const clearTelegramAttachment = () => {
    if (telegramAttachmentPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(telegramAttachmentPreviewUrl);
    }
    setTelegramAttachmentType("none");
    setTelegramAttachmentUrl("");
    setTelegramAttachmentFileName("");
    setTelegramAttachmentPreviewUrl("");
    setTelegramAttachmentPreviewKind("none");
    setTelegramUploadProgress(0);
  };

  const handleCloseTelegramBroadcastModal = () => {
    telegramUploadAbortController.current?.abort();
    telegramUploadAbortController.current = null;
    telegramRecordingCanceledRef.current = true;
    if (
      telegramMediaRecorderRef.current &&
      telegramMediaRecorderRef.current.state !== "inactive"
    ) {
      telegramMediaRecorderRef.current.stop();
    }
    clearIntervalTimer(telegramRecordingTimerRef);
    stopMediaStreamTracks(telegramMediaStreamRef);
    telegramMediaRecorderRef.current = null;
    setIsTelegramRecording(false);
    setTelegramRecordingSeconds(0);
    setIsTelegramBroadcastModalOpen(false);
    setTelegramBroadcastMessage("");
    setTelegramParseMode(undefined);
    setTelegramDisableWebPagePreview(false);
    setTelegramBroadcastTarget("all");
    clearTelegramAttachment();
  };

  const uploadTelegramAttachmentFile = async (
    file: File,
    previewKindOverride?: "image" | "video" | "voice" | "file",
  ) => {
    const abortController = new AbortController();
    telegramUploadAbortController.current = abortController;
    setIsTelegramUploading(true);

    const previewUrl = URL.createObjectURL(file);
    const previewKind = previewKindOverride
      ? previewKindOverride
      : file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : file.type.startsWith("audio/")
            ? "voice"
            : "file";
    const derivedAttachmentType: TelegramAttachmentKind =
      previewKind === "voice" ? "voice" : previewKind;

    clearTelegramAttachment();
    setTelegramAttachmentFileName(file.name);
    setTelegramAttachmentPreviewUrl(previewUrl);
    setTelegramAttachmentPreviewKind(previewKind);
    setTelegramAttachmentType(derivedAttachmentType);
    setTelegramUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        "/admin/miniapp-notifications/upload-attachment",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          signal: abortController.signal,
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setTelegramUploadProgress(Math.max(0, Math.min(100, progress)));
          },
        },
      );

      const isAudioUpload =
        previewKind === "voice" || file.type.startsWith("audio/");
      const responseAttachmentType = isAudioUpload
        ? "voice"
        : response.data?.attachmentType === "image" ||
            response.data?.attachmentType === "video" ||
            response.data?.attachmentType === "file" ||
            response.data?.attachmentType === "voice"
          ? response.data.attachmentType
          : derivedAttachmentType;
      setTelegramAttachmentType(responseAttachmentType);
      setTelegramAttachmentUrl(response.data?.attachmentUrl || "");
      setTelegramAttachmentFileName(file.name);
      setTelegramUploadProgress(100);
    } catch (error) {
      const err = error as { code?: string };
      if (err?.code === "ERR_CANCELED") {
        setTelegramSnackbarMessage("Upload canceled.");
        setTelegramSnackbarSeverity("error");
        setTelegramSnackbarOpen(true);
        clearTelegramAttachment();
        return;
      }
      console.error("Failed to upload telegram attachment:", error);
      setTelegramSnackbarMessage("Failed to upload attachment.");
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
      clearTelegramAttachment();
    } finally {
      telegramUploadAbortController.current = null;
      setIsTelegramUploading(false);
    }
  };

  const handleUploadTelegramAttachment = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadTelegramAttachmentFile(file);
    event.target.value = "";
  };

  const handleStartTelegramVoiceRecording = async () => {
    if (
      isTelegramRecording ||
      isTelegramUploading ||
      isTelegramBroadcastSending
    )
      return;
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setTelegramSnackbarMessage(
        "Voice recording is not supported in this browser.",
      );
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
      return;
    }

    try {
      telegramRecordingCanceledRef.current = false;
      clearTelegramAttachment();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      telegramMediaStreamRef.current = stream;
      const mimeType = resolveVoiceMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      telegramMediaRecorderRef.current = mediaRecorder;
      telegramRecordingChunksRef.current = [];
      setTelegramRecordingSeconds(0);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          telegramRecordingChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        clearIntervalTimer(telegramRecordingTimerRef);
        stopMediaStreamTracks(telegramMediaStreamRef);
        setIsTelegramRecording(false);

        if (telegramRecordingCanceledRef.current) {
          telegramRecordingChunksRef.current = [];
          telegramRecordingCanceledRef.current = false;
          setTelegramRecordingSeconds(0);
          return;
        }

        const voiceBlob = new Blob(telegramRecordingChunksRef.current, {
          type: mediaRecorder.mimeType || "audio/webm",
        });
        telegramRecordingChunksRef.current = [];
        setTelegramRecordingSeconds(0);
        if (!voiceBlob.size) return;

        const ext = voiceBlob.type.includes("ogg") ? "ogg" : "webm";
        const voiceFile = new File(
          [voiceBlob],
          `telegram-voice-${Date.now()}.${ext}`,
          {
            type: voiceBlob.type || "audio/webm",
          },
        );
        await uploadTelegramAttachmentFile(voiceFile, "voice");
      };

      mediaRecorder.start();
      setIsTelegramRecording(true);
      telegramRecordingTimerRef.current = setInterval(() => {
        setTelegramRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Failed to start telegram voice recording:", error);
      setTelegramSnackbarMessage(
        "Microphone access failed. Please allow mic permission.",
      );
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
      clearIntervalTimer(telegramRecordingTimerRef);
      stopMediaStreamTracks(telegramMediaStreamRef);
      setIsTelegramRecording(false);
      setTelegramRecordingSeconds(0);
    }
  };

  const handleStopTelegramVoiceRecording = () => {
    if (
      !telegramMediaRecorderRef.current ||
      telegramMediaRecorderRef.current.state === "inactive"
    )
      return;
    telegramMediaRecorderRef.current.stop();
  };

  const handleCancelTelegramVoiceRecording = () => {
    telegramRecordingCanceledRef.current = true;
    if (
      telegramMediaRecorderRef.current &&
      telegramMediaRecorderRef.current.state !== "inactive"
    ) {
      telegramMediaRecorderRef.current.stop();
    }
    clearIntervalTimer(telegramRecordingTimerRef);
    stopMediaStreamTracks(telegramMediaStreamRef);
    setIsTelegramRecording(false);
    setTelegramRecordingSeconds(0);
  };

  const handleCancelTelegramUpload = () => {
    telegramUploadAbortController.current?.abort();
  };

  const applyTelegramFormatting = (
    openingTag: string,
    closingTag: string,
    placeholder: string,
  ) => {
    const input = telegramMessageInputRef.current;
    const start = input?.selectionStart ?? telegramBroadcastMessage.length;
    const end = input?.selectionEnd ?? start;
    const selected = telegramBroadcastMessage.slice(start, end) || placeholder;
    const formatted = `${openingTag}${selected}${closingTag}`;
    const nextMessage =
      telegramBroadcastMessage.slice(0, start) +
      formatted +
      telegramBroadcastMessage.slice(end);

    setTelegramBroadcastMessage(nextMessage);
    setTelegramParseMode("HTML");

    requestAnimationFrame(() => {
      input?.focus();
      const selectionStart = start + openingTag.length;
      input?.setSelectionRange(
        selectionStart,
        selectionStart + selected.length,
      );
    });
  };

  const applyTelegramLink = () => {
    const url = window.prompt("Enter the full link (https://...):");
    if (!url?.trim()) return;
    const escapedUrl = url
      .trim()
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;");
    applyTelegramFormatting(`<a href="${escapedUrl}">`, "</a>", "link text");
  };

  const handleSendTelegramBroadcast = async () => {
    if (!telegramBroadcastMessage.trim() && !telegramAttachmentUrl.trim())
      return;
    if (telegramBroadcastTarget === "selected" && selectedUserIds.length === 0)
      return;

    setIsTelegramBroadcastSending(true);
    try {
      const payload = {
        allUsers: telegramBroadcastTarget === "all",
        userIds:
          telegramBroadcastTarget === "selected" ? selectedUserIds : undefined,
        message: telegramBroadcastMessage.trim(),
        attachmentType: telegramAttachmentType,
        attachmentUrl: telegramAttachmentUrl || undefined,
        parseMode: telegramParseMode,
        disableWebPagePreview: telegramDisableWebPagePreview,
      };
      await axios.post("/admin/broadcast-telegram-message", payload);

      setTelegramSnackbarMessage("Telegram bot broadcast queued successfully!");
      setTelegramSnackbarSeverity("success");
      setTelegramSnackbarOpen(true);
      handleCloseTelegramBroadcastModal();
    } catch (error) {
      console.error("Failed to send telegram bot broadcast:", error);
      setTelegramSnackbarMessage(
        "Failed to send telegram bot broadcast. Please try again.",
      );
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
    } finally {
      setIsTelegramBroadcastSending(false);
    }
  };

  const handleOpenNotificationModal = () => {
    setNotificationTitle("");
    setNotificationMessage("");
    setNotificationLink("");
    setNotificationLinkText("");
    setNotificationAttachmentType("none");
    setNotificationAttachmentUrl("");
    setNotificationAttachmentFileName("");
    setNotificationAttachmentPreviewUrl("");
    setNotificationAttachmentPreviewKind("none");
    setNotificationUploadProgress(0);
    setIsNotificationRecording(false);
    setNotificationRecordingSeconds(0);
    setNotificationAttachments([]);
    setNotificationTarget(selectedUserIds.length > 0 ? "selected" : "all");
    setIsNotificationModalOpen(true);
  };

  const clearNotificationAttachment = () => {
    notificationAttachments.forEach((item) => {
      if (item.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    if (notificationAttachmentPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(notificationAttachmentPreviewUrl);
    }
    setNotificationAttachmentType("none");
    setNotificationAttachmentUrl("");
    setNotificationAttachmentFileName("");
    setNotificationAttachmentPreviewUrl("");
    setNotificationAttachmentPreviewKind("none");
    setNotificationAttachments([]);
    setNotificationUploadProgress(0);
  };

  const removeNotificationAttachment = (index: number) => {
    setNotificationAttachments((prev) => {
      const target = prev[index];
      if (target?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(target.previewUrl);
      }
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      const firstAttachment = next[0];
      setNotificationAttachmentType(firstAttachment?.attachmentType || "none");
      setNotificationAttachmentUrl(firstAttachment?.attachmentUrl || "");
      setNotificationAttachmentFileName(firstAttachment?.fileName || "");
      setNotificationAttachmentPreviewUrl(firstAttachment?.previewUrl || "");
      setNotificationAttachmentPreviewKind(
        firstAttachment?.previewKind || "none",
      );
      return next;
    });
  };

  const handleCloseNotificationModal = () => {
    notificationUploadAbortController.current?.abort();
    notificationUploadAbortController.current = null;
    notificationRecordingCanceledRef.current = true;
    if (
      notificationMediaRecorderRef.current &&
      notificationMediaRecorderRef.current.state !== "inactive"
    ) {
      notificationMediaRecorderRef.current.stop();
    }
    clearIntervalTimer(notificationRecordingTimerRef);
    stopMediaStreamTracks(notificationMediaStreamRef);
    notificationMediaRecorderRef.current = null;
    setIsNotificationRecording(false);
    setNotificationRecordingSeconds(0);
    setIsNotificationModalOpen(false);
    setNotificationTitle("");
    setNotificationMessage("");
    clearNotificationAttachment();
  };

  const uploadNotificationAttachmentFile = async (
    file: File,
    previewKindOverride?: NotificationAttachmentKind,
  ) => {
    const abortController = new AbortController();
    notificationUploadAbortController.current = abortController;
    setIsNotificationUploading(true);

    const previewUrl = URL.createObjectURL(file);
    const previewKind: NotificationAttachmentKind = previewKindOverride
      ? previewKindOverride
      : file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("audio/")
          ? "voice"
          : file.type.startsWith("video/")
            ? "video"
            : "file";

    if (notificationAttachmentPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(notificationAttachmentPreviewUrl);
    }
    setNotificationAttachmentFileName(file.name);
    setNotificationAttachmentPreviewUrl(previewUrl);
    setNotificationAttachmentPreviewKind(previewKind);
    setNotificationAttachmentType(previewKind);
    setNotificationUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        "/admin/miniapp-notifications/upload-attachment",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          signal: abortController.signal,
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setNotificationUploadProgress(Math.max(0, Math.min(100, progress)));
          },
        },
      );

      const responseAttachmentType =
        response.data?.attachmentType === "image" ||
        response.data?.attachmentType === "video" ||
        response.data?.attachmentType === "file" ||
        response.data?.attachmentType === "voice"
          ? response.data.attachmentType
          : previewKind === "none"
            ? "file"
            : previewKind;
      setNotificationAttachmentType(responseAttachmentType);
      setNotificationAttachmentUrl(response.data.attachmentUrl || "");
      setNotificationAttachmentFileName(file.name);
      setNotificationAttachmentPreviewUrl(previewUrl);
      setNotificationAttachmentPreviewKind(previewKind);
      setNotificationAttachments((prev) => [
        ...prev,
        {
          attachmentType: responseAttachmentType,
          attachmentUrl: response.data.attachmentUrl || "",
          fileName: file.name,
          previewUrl,
          previewKind,
        },
      ]);
      setNotificationUploadProgress(100);
    } catch (error) {
      const err = error as { code?: string };
      if (err?.code === "ERR_CANCELED") {
        setNotificationSnackbarMessage("Upload canceled.");
        setNotificationSnackbarSeverity("error");
        setNotificationSnackbarOpen(true);
        if (previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
        return;
      }
      console.error("Failed to upload notification attachment:", error);
      setNotificationSnackbarMessage("Failed to upload attachment.");
      setNotificationSnackbarSeverity("error");
      setNotificationSnackbarOpen(true);
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    } finally {
      notificationUploadAbortController.current = null;
      setIsNotificationUploading(false);
    }
  };

  const handleUploadNotificationAttachment = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadNotificationAttachmentFile(file);
    event.target.value = "";
  };

  const handleStartNotificationVoiceRecording = async () => {
    if (
      isNotificationRecording ||
      isNotificationUploading ||
      isNotificationSending
    )
      return;
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setNotificationSnackbarMessage(
        "Voice recording is not supported in this browser.",
      );
      setNotificationSnackbarSeverity("error");
      setNotificationSnackbarOpen(true);
      return;
    }

    try {
      notificationRecordingCanceledRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      notificationMediaStreamRef.current = stream;
      const mimeType = resolveVoiceMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      notificationMediaRecorderRef.current = mediaRecorder;
      notificationRecordingChunksRef.current = [];
      setNotificationRecordingSeconds(0);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          notificationRecordingChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        clearIntervalTimer(notificationRecordingTimerRef);
        stopMediaStreamTracks(notificationMediaStreamRef);
        setIsNotificationRecording(false);

        if (notificationRecordingCanceledRef.current) {
          notificationRecordingChunksRef.current = [];
          notificationRecordingCanceledRef.current = false;
          setNotificationRecordingSeconds(0);
          return;
        }

        const voiceBlob = new Blob(notificationRecordingChunksRef.current, {
          type: mediaRecorder.mimeType || "audio/webm",
        });
        notificationRecordingChunksRef.current = [];
        setNotificationRecordingSeconds(0);
        if (!voiceBlob.size) return;

        const ext = voiceBlob.type.includes("ogg") ? "ogg" : "webm";
        const voiceFile = new File(
          [voiceBlob],
          `miniapp-voice-${Date.now()}.${ext}`,
          {
            type: voiceBlob.type || "audio/webm",
          },
        );
        await uploadNotificationAttachmentFile(voiceFile, "voice");
      };

      mediaRecorder.start();
      setIsNotificationRecording(true);
      notificationRecordingTimerRef.current = setInterval(() => {
        setNotificationRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Failed to start mini app voice recording:", error);
      setNotificationSnackbarMessage(
        "Microphone access failed. Please allow mic permission.",
      );
      setNotificationSnackbarSeverity("error");
      setNotificationSnackbarOpen(true);
      clearIntervalTimer(notificationRecordingTimerRef);
      stopMediaStreamTracks(notificationMediaStreamRef);
      setIsNotificationRecording(false);
      setNotificationRecordingSeconds(0);
    }
  };

  const handleStopNotificationVoiceRecording = () => {
    if (
      !notificationMediaRecorderRef.current ||
      notificationMediaRecorderRef.current.state === "inactive"
    )
      return;
    notificationMediaRecorderRef.current.stop();
  };

  const handleCancelNotificationVoiceRecording = () => {
    notificationRecordingCanceledRef.current = true;
    if (
      notificationMediaRecorderRef.current &&
      notificationMediaRecorderRef.current.state !== "inactive"
    ) {
      notificationMediaRecorderRef.current.stop();
    }
    clearIntervalTimer(notificationRecordingTimerRef);
    stopMediaStreamTracks(notificationMediaStreamRef);
    setIsNotificationRecording(false);
    setNotificationRecordingSeconds(0);
  };

  const handleCancelNotificationUpload = () => {
    notificationUploadAbortController.current?.abort();
  };

  const handleSendNotification = async () => {
    if (!notificationTitle.trim()) return;
    if (!notificationMessage.trim() && notificationAttachments.length === 0)
      return;
    if (notificationTarget === "selected" && selectedUserIds.length === 0)
      return;

    setIsNotificationSending(true);
    try {
      const normalizedAttachments = notificationAttachments
        .filter((item) => item.attachmentUrl)
        .map((item) => ({
          attachmentType:
            item.attachmentType === "voice" ? "file" : item.attachmentType,
          attachmentUrl: item.attachmentUrl,
        }));
      const firstAttachment = normalizedAttachments[0];
      await axios.post("/admin/broadcast-miniapp-notification", {
        allUsers: notificationTarget === "all",
        userIds:
          notificationTarget === "selected" ? selectedUserIds : undefined,
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        link: notificationLink.trim() || undefined,
        linkText: notificationLink.trim() ? notificationLinkText.trim() || undefined : undefined,
        attachmentType: firstAttachment?.attachmentType || "none",
        attachmentUrl: firstAttachment?.attachmentUrl || undefined,
        attachments: normalizedAttachments,
      });

      setNotificationSnackbarMessage("Mini app broadcast sent successfully!");
      setNotificationSnackbarSeverity("success");
      setNotificationSnackbarOpen(true);
      handleCloseNotificationModal();
    } catch (error) {
      console.error("Failed to send notification:", error);
      setNotificationSnackbarMessage(
        "Failed to send mini app broadcast. Please try again.",
      );
      setNotificationSnackbarSeverity("error");
      setNotificationSnackbarOpen(true);
    } finally {
      setIsNotificationSending(false);
    }
  };

  const handleCloseNotificationSnackbar = () => {
    setNotificationSnackbarOpen(false);
  };

  useEffect(() => {
    return () => {
      clearIntervalTimer(telegramRecordingTimerRef);
      clearIntervalTimer(notificationRecordingTimerRef);
      stopMediaStreamTracks(telegramMediaStreamRef);
      stopMediaStreamTracks(notificationMediaStreamRef);
      if (telegramAttachmentPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(telegramAttachmentPreviewUrl);
      }
      if (notificationAttachmentPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(notificationAttachmentPreviewUrl);
      }
      notificationAttachments.forEach((item) => {
        if (item.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [
    notificationAttachmentPreviewUrl,
    notificationAttachments,
    telegramAttachmentPreviewUrl,
  ]);

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 4 },
      }}
    >
      <Card
        sx={{
          p: { xs: 2, sm: 4 },
          m: { xs: 2, sm: 4 },
        }}
      >
        <UsersMixedChart />
      </Card>
      <Box display="flex" flexDirection="column" gap={3}>
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 2 : 0}
        >
          <Typography variant="h5" fontWeight="bold" color="text.secondary">
            Users
          </Typography>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            invisible={!hasActiveFilters}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: "bold",
              },
            }}
          >
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                startIcon={<FilterListIcon />}
                variant={hasActiveFilters ? "contained" : "outlined"}
                color="primary"
                onClick={openFilterModal}
                sx={{
                  height: "40px",
                  borderRadius: 1.5,
                }}
              >
                Filters
              </Button>
              <Button
                startIcon={<NotificationsIcon />}
                variant="outlined"
                color="primary"
                onClick={handleOpenNotificationModal}
                sx={{
                  height: "40px",
                  borderRadius: 1.5,
                }}
              >
                Mini App Broadcast
              </Button>
              <Button
                startIcon={<TelegramIcon />}
                variant="outlined"
                color="primary"
                onClick={handleOpenTelegramBroadcastModal}
                sx={{
                  height: "40px",
                  borderRadius: 1.5,
                }}
              >
                Telegram Bot Broadcast
              </Button>
              {selectedUserIds.length > 0 && (
                <Chip
                  color="primary"
                  variant="outlined"
                  label={`${selectedUserIds.length} selected`}
                  onDelete={() => setSelectedUserIds([])}
                />
              )}
            </Box>
          </Badge>
        </Box>

        <TextField
          size="small"
          fullWidth
          label="Search user"
          placeholder="Telegram ID, wallet, or username"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            setSelectedUserIds([]);
            setPage(1);
          }}
        />

        {hasActiveFilters && (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Button
              size="small"
              variant={allFilteredSelected ? "outlined" : "contained"}
              onClick={handleToggleSelectAllFiltered}
              disabled={
                totalFilteredUsers === 0 || isFetching || isSelectingAllFiltered
              }
              startIcon={
                isSelectingAllFiltered ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {allFilteredSelected
                ? `Clear all ${totalFilteredUsers.toLocaleString()} selected users`
                : `Select all ${totalFilteredUsers.toLocaleString()} filtered users`}
            </Button>
            {fromDate && (
              <Chip
                label={`From: ${fromDate.format("MMM DD, YYYY")}`}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={handleRemoveFromDate}
              />
            )}
            {toDate && (
              <Chip
                label={`To: ${toDate.format("MMM DD, YYYY")}`}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={handleRemoveToDate}
              />
            )}
            {debouncedSearch.trim() && (
              <Chip
                label={`Search: ${debouncedSearch}`}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={handleRemoveSearch}
              />
            )}
            {watchedCourseItemId && (
              <Chip
                label={`Watched: ${
                  watchedCourseItemLabel || `Item ${watchedCourseItemId}`
                }`}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={handleRemoveWatchedCourseItem}
              />
            )}
          </Stack>
        )}

        <Paper
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
            width: "100%",
          }}
        >
          {isError ? (
            <Box p={3} textAlign="center">
              <Typography color="error" variant="body1">
                🚨 {(error as Error).message}
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box p={2}>
              {[...Array(4)].map((_, index) => (
                <Box key={index} display="flex" gap={2} py={2} px={2}>
                  <Skeleton variant="text" width="15%" height={30} />
                  <Skeleton variant="text" width="25%" height={30} />
                  <Skeleton variant="text" width="25%" height={30} />
                  <Skeleton variant="text" width="25%" height={30} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                overflowX: "auto",
                "& .MuiTable-root": {
                  minWidth: isMobile ? "800px" : "100%",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        width: "56px",
                      }}
                    >
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={
                          !allVisibleSelected && someVisibleSelected
                        }
                        onChange={handleToggleSelectAllVisible}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "100px",
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "150px",
                      }}
                    >
                      Telegram ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "150px",
                      }}
                    >
                      Username
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "200px",
                      }}
                    >
                      Joined At
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "250px",
                      }}
                    >
                      Midas Wallet Address
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "150px",
                      }}
                    >
                      Campaign
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        minWidth: "100px",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user: User, index: number) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        backgroundColor:
                          "background.paper",
                      }}
                    >
                      <TableCell sx={{ width: "56px" }}>
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleToggleUserSelection(user.id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: "100px" }}>
                        {user.id}
                      </TableCell>
                      <TableCell sx={{ minWidth: "150px" }}>
                        {user.telegramId}
                      </TableCell>
                      <TableCell sx={{ minWidth: "150px" }}>
                        {user.username}
                      </TableCell>
                      <TableCell sx={{ minWidth: "200px" }}>
                        {new Date(user.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell sx={{ minWidth: "250px" }}>
                        {user.midasWalletAddress ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title={user.midasWalletAddress}>
                              <Typography noWrap sx={{ maxWidth: "200px" }}>
                                {truncateAddress(user.midasWalletAddress)}
                              </Typography>
                            </Tooltip>
                            <Tooltip title="Copy wallet address">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  user.midasWalletAddress &&
                                  handleCopyWalletAddress(
                                    user.midasWalletAddress,
                                  )
                                }
                                sx={{
                                  p: 0.5,
                                  backgroundColor: "rgba(25, 118, 210, 0.05)",
                                  "&:hover": {
                                    backgroundColor: "rgba(25, 118, 210, 0.15)",
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            fontStyle="italic"
                          >
                            No wallet address
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: "150px" }}>
                        {user.campaignName || "main_channel"}
                      </TableCell>
                      <TableCell sx={{ minWidth: "100px" }}>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Referrals">
                            <IconButton
                              onClick={() => navigate(`/referrals/${user.id}`)}
                              size="small"
                              color="primary"
                              sx={{
                                backgroundColor: "rgba(25, 118, 210, 0.05)",
                                "&:hover": {
                                  backgroundColor: "rgba(25, 118, 210, 0.15)",
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            color="primary"
            size={isMobile ? "small" : "large"}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
      </Box>

      {/* Filter Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={closeFilterModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Filter Users</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              Date Range
            </Typography>
            <Box
              display="flex"
              gap={2}
              flexDirection={isMobile ? "column" : "row"}
            >
              <DatePicker
                label="From Date"
                value={tempFromDate}
                onChange={(date) => setTempFromDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="To Date"
                value={tempToDate}
                onChange={(date) => setTempToDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Box>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              Watched Course Item
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={-2}>
              Show users who have opened the selected course item at least once.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="watched-course-filter-label">Course</InputLabel>
              <Select
                labelId="watched-course-filter-label"
                label="Course"
                value={tempWatchedCourseId ?? ""}
                onChange={(event) => {
                  const courseId = Number(event.target.value);
                  setTempWatchedCourseId(courseId || null);
                  setTempWatchedSeasonId(null);
                  setTempWatchedCourseItemId(null);
                }}
                endAdornment={
                  areCoursesLoading ? <CircularProgress size={18} /> : null
                }
              >
                {filterCourses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={!tempWatchedCourseId || areSeasonsLoading}
            >
              <InputLabel id="watched-season-filter-label">Season</InputLabel>
              <Select
                labelId="watched-season-filter-label"
                label="Season"
                value={tempWatchedSeasonId ?? ""}
                onChange={(event) => {
                  const seasonId = Number(event.target.value);
                  setTempWatchedSeasonId(seasonId || null);
                  setTempWatchedCourseItemId(null);
                }}
                endAdornment={
                  areSeasonsLoading ? <CircularProgress size={18} /> : null
                }
              >
                {filterSeasons.map((season) => (
                  <MenuItem key={season.id} value={season.id}>
                    {season.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={!tempWatchedSeasonId || areItemsLoading}
            >
              <InputLabel id="watched-item-filter-label">
                Course item
              </InputLabel>
              <Select
                labelId="watched-item-filter-label"
                label="Course item"
                value={tempWatchedCourseItemId ?? ""}
                onChange={(event) => {
                  const courseItemId = Number(event.target.value);
                  setTempWatchedCourseItemId(courseItemId || null);
                }}
                endAdornment={
                  areItemsLoading ? <CircularProgress size={18} /> : null
                }
              >
                {filterCourseItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={clearFilters} color="inherit">
            Clear All
          </Button>
          <Button onClick={closeFilterModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={applyFilters}
            disabled={Boolean(tempWatchedCourseId && !tempWatchedCourseItemId)}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Telegram Message Modal */}
      <Dialog
        open={isTelegramModalOpen}
        onClose={handleCloseTelegramModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TelegramIcon />
          Send Telegram Message
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography variant="body2" color="text.secondary">
              Send a message to user ID: <strong>{selectedUserId}</strong>
            </Typography>
            <TextField
              label="Message"
              multiline
              rows={4}
              fullWidth
              value={telegramMessage}
              onChange={(e) => setTelegramMessage(e.target.value)}
              placeholder="Enter your message here..."
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 1.5,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseTelegramModal}
            color="inherit"
            disabled={isSendingMessage}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendTelegramMessage}
            variant="contained"
            color="primary"
            disabled={!telegramMessage.trim() || isSendingMessage}
            sx={{
              borderRadius: 1.5,
              minWidth: "100px",
            }}
          >
            {isSendingMessage ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Telegram Bot Broadcast Modal */}
      <Dialog
        open={isTelegramBroadcastModalOpen}
        onClose={handleCloseTelegramBroadcastModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TelegramIcon />
          Telegram Bot Broadcast
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography variant="body2" color="text.secondary">
              Send a Telegram PM broadcast to all users or only selected users.
            </Typography>
            <RadioGroup
              value={telegramBroadcastTarget}
              onChange={(e) =>
                setTelegramBroadcastTarget(e.target.value as "all" | "selected")
              }
              row
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All users"
              />
              <FormControlLabel
                value="selected"
                control={<Radio />}
                label={`Selected users (${selectedUserIds.length})`}
                disabled={selectedUserIds.length === 0}
              />
            </RadioGroup>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.75}
              >
                Select text, then apply Telegram formatting:
              </Typography>
              <Box display="flex" gap={0.75} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting("<b>", "</b>", "bold text")
                  }
                  sx={{ fontWeight: 800, minWidth: 38 }}
                >
                  B
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting("<i>", "</i>", "italic text")
                  }
                  sx={{ fontStyle: "italic", minWidth: 38 }}
                >
                  I
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting("<u>", "</u>", "underlined text")
                  }
                  sx={{ textDecoration: "underline", minWidth: 38 }}
                >
                  U
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting("<s>", "</s>", "struck text")
                  }
                  sx={{ textDecoration: "line-through", minWidth: 38 }}
                >
                  S
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting(
                      "<tg-spoiler>",
                      "</tg-spoiler>",
                      "spoiler",
                    )
                  }
                >
                  Spoiler
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting("<code>", "</code>", "code")
                  }
                >
                  Code
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    applyTelegramFormatting(
                      "<blockquote>",
                      "</blockquote>",
                      "quoted text",
                    )
                  }
                >
                  Quote
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={applyTelegramLink}
                >
                  Link
                </Button>
                {telegramParseMode && (
                  <Chip size="small" color="primary" label="HTML styling on" />
                )}
              </Box>
            </Box>
            <TextField
              inputRef={telegramMessageInputRef}
              label="Message"
              multiline
              rows={5}
              fullWidth
              value={telegramBroadcastMessage}
              onChange={(e) => setTelegramBroadcastMessage(e.target.value)}
              placeholder="Enter Telegram message..."
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 1.5,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={telegramDisableWebPagePreview}
                  onChange={(event) =>
                    setTelegramDisableWebPagePreview(event.target.checked)
                  }
                />
              }
              label="Hide link previews"
            />
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {!isTelegramRecording ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleStartTelegramVoiceRecording}
                  disabled={isTelegramUploading || isTelegramBroadcastSending}
                >
                  Record Voice
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleStopTelegramVoiceRecording}
                  >
                    Stop Recording
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleCancelTelegramVoiceRecording}
                  >
                    Discard
                  </Button>
                  <Typography variant="caption" color="error" fontWeight={700}>
                    Recording... {formatRecordingTime(telegramRecordingSeconds)}
                  </Typography>
                </>
              )}
            </Box>
            <Button
              variant="outlined"
              component="label"
              disabled={isTelegramUploading || isTelegramBroadcastSending}
              sx={{ borderRadius: 1.5, justifyContent: "flex-start" }}
            >
              {isTelegramUploading
                ? "Uploading..."
                : "Upload Image, Video, Voice, or File"}
              <input
                hidden
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.rar,.txt"
                onChange={handleUploadTelegramAttachment}
              />
            </Button>
            {isTelegramUploading && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={telegramUploadProgress}
                  sx={{ borderRadius: 1 }}
                />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Uploading: {telegramUploadProgress}%
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleCancelTelegramUpload}
                  >
                    Cancel Upload
                  </Button>
                </Box>
              </Box>
            )}
            {telegramAttachmentFileName && (
              <Box display="flex" alignItems="center" gap={1.5}>
                {telegramAttachmentPreviewKind === "image" &&
                  telegramAttachmentPreviewUrl && (
                    <Box
                      component="img"
                      src={telegramAttachmentPreviewUrl}
                      alt={telegramAttachmentFileName}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1,
                        objectFit: "cover",
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    />
                  )}
                {telegramAttachmentPreviewKind === "voice" &&
                  telegramAttachmentPreviewUrl && (
                    <Box
                      component="audio"
                      controls
                      src={telegramAttachmentPreviewUrl}
                      sx={{ width: "100%", maxWidth: 260 }}
                    />
                  )}
                {telegramAttachmentPreviewKind === "video" &&
                  telegramAttachmentPreviewUrl && (
                    <Box
                      component="video"
                      controls
                      src={telegramAttachmentPreviewUrl}
                      sx={{ width: "100%", maxWidth: 260, borderRadius: 1 }}
                    />
                  )}
                {telegramAttachmentPreviewKind === "file" && (
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 1,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      color: "text.secondary",
                      backgroundColor: "background.paper",
                    }}
                  >
                    FILE
                  </Box>
                )}
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {telegramAttachmentUrl ? "Uploaded:" : "Selected:"}{" "}
                    {telegramAttachmentFileName}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={clearTelegramAttachment}
                    disabled={isTelegramUploading}
                    sx={{ px: 0, minWidth: "auto" }}
                  >
                    Remove Attachment
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseTelegramBroadcastModal}
            color="inherit"
            disabled={isTelegramBroadcastSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendTelegramBroadcast}
            variant="contained"
            color="primary"
            disabled={
              (!telegramBroadcastMessage.trim() &&
                !telegramAttachmentUrl.trim()) ||
              (telegramBroadcastTarget === "selected" &&
                selectedUserIds.length === 0) ||
              isTelegramUploading ||
              isTelegramRecording ||
              isTelegramBroadcastSending
            }
            sx={{
              borderRadius: 1.5,
              minWidth: "120px",
            }}
          >
            {isTelegramBroadcastSending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mini App Broadcast Modal */}
      <Dialog
        open={isNotificationModalOpen}
        onClose={handleCloseNotificationModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <NotificationsIcon />
          Mini App Broadcast
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography variant="body2" color="text.secondary">
              Send a mini app notification to selected users or all users.
            </Typography>
            <RadioGroup
              value={notificationTarget}
              onChange={(e) =>
                setNotificationTarget(e.target.value as "all" | "selected")
              }
              row
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All users"
              />
              <FormControlLabel
                value="selected"
                control={<Radio />}
                label={`Selected users (${selectedUserIds.length})`}
                disabled={selectedUserIds.length === 0}
              />
            </RadioGroup>
            <TextField
              label="Title"
              fullWidth
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="Enter notification title..."
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 1.5,
                },
              }}
            />
            <TextField
              label="Message"
              multiline
              rows={4}
              fullWidth
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="Enter notification message..."
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 1.5,
                },
              }}
            />
            <TextField
              label="Link (optional)"
              fullWidth
              value={notificationLink}
              onChange={(e) => setNotificationLink(e.target.value)}
              placeholder="https://... — shown as a button on the notification"
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 1.5,
                },
              }}
            />
            {notificationLink.trim() && (
              <TextField
                label="Button text (optional)"
                fullWidth
                value={notificationLinkText}
                onChange={(e) => setNotificationLinkText(e.target.value)}
                placeholder='Defaults to "View" if left blank'
                variant="outlined"
                inputProps={{ maxLength: 64 }}
                InputProps={{
                  sx: {
                    borderRadius: 1.5,
                  },
                }}
              />
            )}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {!isNotificationRecording ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleStartNotificationVoiceRecording}
                  disabled={isNotificationUploading || isNotificationSending}
                >
                  Record Voice
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleStopNotificationVoiceRecording}
                  >
                    Stop Recording
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleCancelNotificationVoiceRecording}
                  >
                    Discard
                  </Button>
                  <Typography variant="caption" color="error" fontWeight={700}>
                    Recording...{" "}
                    {formatRecordingTime(notificationRecordingSeconds)}
                  </Typography>
                </>
              )}
            </Box>
            <Button
              variant="outlined"
              component="label"
              disabled={isNotificationUploading || isNotificationSending}
              sx={{ borderRadius: 1.5, justifyContent: "flex-start" }}
            >
              {isNotificationUploading
                ? "Uploading..."
                : "Upload Attachment (Image/Video/Voice/File)"}
              <input
                hidden
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.rar,.txt"
                onChange={handleUploadNotificationAttachment}
              />
            </Button>
            {isNotificationUploading && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={notificationUploadProgress}
                  sx={{ borderRadius: 1 }}
                />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Uploading: {notificationUploadProgress}%
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleCancelNotificationUpload}
                  >
                    Cancel Upload
                  </Button>
                </Box>
              </Box>
            )}
            {notificationAttachments.length > 0 && (
              <Box display="flex" flexDirection="column" gap={1}>
                {notificationAttachments.map((attachment, index) => (
                  <Box
                    key={`${attachment.fileName}-${attachment.attachmentUrl}-${index}`}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                  >
                    {attachment.previewKind === "image" &&
                      attachment.previewUrl && (
                        <Box
                          component="img"
                          src={attachment.previewUrl}
                          alt={attachment.fileName}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            objectFit: "cover",
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                          }}
                        />
                      )}
                    {attachment.previewKind === "video" &&
                      attachment.previewUrl && (
                        <Box
                          component="video"
                          src={attachment.previewUrl}
                          sx={{
                            width: 72,
                            height: 56,
                            borderRadius: 1,
                            objectFit: "cover",
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                          }}
                          muted
                        />
                      )}
                    {attachment.previewKind === "voice" &&
                      attachment.previewUrl && (
                        <Box
                          component="audio"
                          controls
                          src={attachment.previewUrl}
                          sx={{ width: "100%", maxWidth: 260 }}
                        />
                      )}
                    {attachment.previewKind === "file" && (
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 1,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          color: "text.secondary",
                          backgroundColor: "background.paper",
                        }}
                      >
                        FILE
                      </Box>
                    )}
                    <Box flex={1}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Uploaded: {attachment.fileName}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeNotificationAttachment(index)}
                        disabled={isNotificationUploading}
                        sx={{ px: 0, minWidth: "auto" }}
                      >
                        Remove Attachment
                      </Button>
                    </Box>
                  </Box>
                ))}
                <Box>
                  <Button
                    size="small"
                    color="error"
                    onClick={clearNotificationAttachment}
                    disabled={isNotificationUploading}
                    sx={{ px: 0, minWidth: "auto" }}
                  >
                    Remove All Attachments
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseNotificationModal}
            color="inherit"
            disabled={isNotificationSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            color="primary"
            disabled={
              !notificationTitle.trim() ||
              (!notificationMessage.trim() &&
                notificationAttachments.length === 0) ||
              (notificationTarget === "selected" &&
                selectedUserIds.length === 0) ||
              isNotificationSending ||
              isNotificationUploading ||
              isNotificationRecording
            }
            sx={{
              borderRadius: 1.5,
              minWidth: "120px",
            }}
          >
            {isNotificationSending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Wallet address copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Telegram Message Snackbar */}
      <Snackbar
        open={telegramSnackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseTelegramSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseTelegramSnackbar}
          severity={telegramSnackbarSeverity}
          sx={{ width: "100%" }}
        >
          {telegramSnackbarMessage}
        </Alert>
      </Snackbar>

      {/* Notification Snackbar */}
      <Snackbar
        open={notificationSnackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseNotificationSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotificationSnackbar}
          severity={notificationSnackbarSeverity}
          sx={{ width: "100%" }}
        >
          {notificationSnackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}
